/**
 * #2089 SIDECFG: the per-user "My links" sidebar section. One row per entry in
 * D1 `user_sidebar_entries` (migrate-2089-user-sidebar-entries.sql), owner-scoped
 * routes under /api/preferences/sidebar-entries (routes/sidebar-entries.ts).
 *
 * This module is the one place the shape and its limits live: the server
 * refuses with these, the client disables with them, and the plain-node test
 * (tests/sidebar-entries-validation-2089-node.test.ts) pins them. An entry is
 * only a pointer: the page it opens is still gated by the role's allowed_pages
 * and the gadget it opens by the artifact routes, never by this table.
 */

export const SIDEBAR_ENTRY_KINDS = ["page", "gadget", "link"] as const;
export type SidebarEntryKind = (typeof SIDEBAR_ENTRY_KINDS)[number];

/** Rows per user. Past this the add routes answer 409. */
export const SIDEBAR_ENTRIES_MAX = 30;
/** Characters in a label after trimming. */
export const SIDEBAR_ENTRY_LABEL_MAX = 60;
/** Characters in a target (a path with its query, an artifact id, or a URL). */
export const SIDEBAR_ENTRY_TARGET_MAX = 500;

export interface SidebarEntry {
  id: number;
  kind: SidebarEntryKind;
  /** page: in-app path with optional query; gadget: artifact id; link: absolute http(s) URL. */
  target: string;
  label: string;
  position: number;
}

export interface SidebarEntryInput {
  kind: string;
  target: string;
  /** Required for page and link; a gadget falls back to the artifact title. */
  label?: string;
}

export function isSidebarEntryKind(kind: unknown): kind is SidebarEntryKind {
  return typeof kind === "string" && (SIDEBAR_ENTRY_KINDS as readonly string[]).includes(kind);
}

/** The label as stored: trimmed, inner whitespace collapsed. */
export function normaliseSidebarLabel(label: unknown): string {
  return typeof label === "string" ? label.replace(/\s+/g, " ").trim() : "";
}

/** Null when the label is storable, else the refusal the client shows. */
export function sidebarLabelError(label: unknown): string | null {
  const clean = normaliseSidebarLabel(label);
  if (!clean) return "A label is required.";
  if (clean.length > SIDEBAR_ENTRY_LABEL_MAX) return `Labels are at most ${SIDEBAR_ENTRY_LABEL_MAX} characters.`;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(clean)) return "A label cannot contain control characters.";
  return null;
}

/** An in-app page target: a root-relative path, optionally with a query or hash.
 *  Never protocol-relative (`//host`), never with whitespace. */
export function sidebarPageTargetError(target: unknown): string | null {
  if (typeof target !== "string" || !target.startsWith("/")) return "A page target must start with /.";
  if (target.startsWith("//")) return "A page target must be a path on this site.";
  if (target.length > SIDEBAR_ENTRY_TARGET_MAX) return `Targets are at most ${SIDEBAR_ENTRY_TARGET_MAX} characters.`;
  if (/\s/.test(target)) return "A page target cannot contain spaces.";
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(target)) return "A page target cannot contain control characters.";
  return null;
}

/** An external link: an absolute URL that parses, http or https only. */
export function sidebarLinkTargetError(target: unknown): string | null {
  if (typeof target !== "string" || !target.trim()) return "A link is required.";
  if (target.length > SIDEBAR_ENTRY_TARGET_MAX) return `Links are at most ${SIDEBAR_ENTRY_TARGET_MAX} characters.`;
  let url: URL;
  try {
    url = new URL(target.trim());
  } catch {
    return "Enter a full web address, starting with https://";
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return "Only http and https links can be saved.";
  if (!url.hostname) return "Enter a full web address, starting with https://";
  return null;
}

/** An artifact id as the artifact routes mint them (uuid-shaped or short slug); ownership is the route's check. */
export function sidebarGadgetTargetError(target: unknown): string | null {
  if (typeof target !== "string" || !target.trim()) return "A gadget id is required.";
  if (!/^[A-Za-z0-9_-]{1,80}$/.test(target)) return "That is not a gadget id.";
  return null;
}

/**
 * The whole input, as POST validates it. Returns the storable row fields, or
 * `{ error }`. A gadget may omit its label (the route reads the artifact title).
 */
export function validateSidebarEntryInput(
  input: SidebarEntryInput,
): { kind: SidebarEntryKind; target: string; label: string | null } | { error: string } {
  if (!isSidebarEntryKind(input.kind)) return { error: "kind must be page, gadget or link." };
  const kind = input.kind;
  const target = typeof input.target === "string" ? input.target.trim() : "";
  const targetError =
    kind === "page" ? sidebarPageTargetError(target)
    : kind === "link" ? sidebarLinkTargetError(target)
    : sidebarGadgetTargetError(target);
  if (targetError) return { error: targetError };
  const label = normaliseSidebarLabel(input.label);
  if (kind === "gadget" && !label) return { kind, target, label: null };
  const labelError = sidebarLabelError(label);
  if (labelError) return { error: labelError };
  return { kind, target, label };
}

/** Where an entry opens: page and link as stored, a gadget on its artifact page. */
export function sidebarEntryHref(entry: Pick<SidebarEntry, "kind" | "target">): string {
  return entry.kind === "gadget" ? `/artifacts/${entry.target}` : entry.target;
}

/** The in-app path the role gate checks for an entry; null for an external link. */
export function sidebarEntryGatePath(entry: Pick<SidebarEntry, "kind" | "target">): string | null {
  if (entry.kind === "link") return null;
  if (entry.kind === "gadget") return "/artifacts";
  const q = entry.target.search(/[?#]/);
  return q === -1 ? entry.target : entry.target.slice(0, q);
}
